import { FileRecord, UploadStatus } from './controller/UploadFileContext'
import { UPLOAD_STATUS } from './controller/uploadStatus'

export const FILE_EXT_TO_TYPE_MAP = {
  pdf: { fileType: 'pdf', hasPreview: true },
  jpg: { fileType: 'img', hasPreview: true },
  jpeg: { fileType: 'img', hasPreview: true },
  png: { fileType: 'img', hasPreview: true },
  gif: { fileType: 'img', hasPreview: true },
  svg: { fileType: 'img', hasPreview: true },
  webp: { fileType: 'img', hasPreview: true },
  bmp: { fileType: 'img', hasPreview: true },
  tiff: { fileType: 'img', hasPreview: true },
  tif: { fileType: 'img', hasPreview: true },
  docx: { fileType: 'doc', hasPreview: false },
  doc: { fileType: 'doc', hasPreview: false },
  xlsx: { fileType: 'sheet', hasPreview: false },
  xls: { fileType: 'sheet', hasPreview: false },
  csv: { fileType: 'sheet', hasPreview: false },
  tsv: { fileType: 'sheet', hasPreview: false },
  other: { fileType: 'other', hasPreview: false },
}

const imgOrPdfFileExtensions = Object.keys(FILE_EXT_TO_TYPE_MAP).filter(
  (t) => FILE_EXT_TO_TYPE_MAP[t].fileType === 'img' || FILE_EXT_TO_TYPE_MAP[t].fileType === 'pdf'
)

export const imgOrPdfTypeExtensionsAsString = imgOrPdfFileExtensions.map((e) => '.' + e).join()

/**
 * Format file size in bytes to a human-readable string
 */
export const formatFileSize = (bytes: number, translate: (key: string) => string): string => {
  if (bytes === 0) return '0 ' + translate('Bytes')
  const k = 1024
  const sizes = [translate('Bytes'), translate('KB'), translate('MB'), translate('GB')]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Get the file size from a FileRecord (from either existing or pending/uploaded record)
 */
export const getFileSize = (record: FileRecord): number | undefined => {
  return record.status === 'existing' ? record.fileRecord?.filesize : record.size
}

/**
 * Format file size from a FileRecord to a human-readable string
 */
export const formatFileRecordSize = (record: FileRecord, translate: (key: string) => string): string => {
  const size = getFileSize(record)
  return size ? formatFileSize(size, translate) : ''
}

/**
 * Check if upload is disabled based on upload status
 */
export const isUploadDisabled = (uploadStatus: UploadStatus): boolean => {
  return uploadStatus === UPLOAD_STATUS.UPLOADING || uploadStatus === UPLOAD_STATUS.UPLOAD_FAILED
}

/**
 * Type guard to check if a URL string is a blob URL (object URL)
 * When this returns true, TypeScript will narrow the type to string
 */
export const isBlobUrl = (url: string | undefined | null): url is string => {
  return typeof url === 'string' && url.startsWith('blob:')
}

/**
 * Create a validator function for file size validation that checks files in UploadFileContext.
 * This validator bridges the UploadFileContext system with react-final-form validation.
 *
 * @param slot - The related slot name
 * @param maxFileSizeBytes - Maximum file size in bytes
 * @param fileRecords - Array of FileRecord from UploadFileContext
 * @param translate - Translation function
 * @returns A validator function compatible with react-final-form
 */
export const createFileSizeValidator = (
  slot: string,
  maxFileSizeBytes: number | undefined,
  fileRecords: FileRecord[],
  translate: (key: string, options?: any) => string
): ((value: any) => string | undefined) => {
  return (value: any) => {
    // If no max size is specified, no validation needed
    if (maxFileSizeBytes === undefined) {
      return undefined
    }

    // Get pending files for this slot from context
    const slotFileRecords = fileRecords.filter((record) => record.slot === slot)
    const pendingFiles = slotFileRecords.filter((record) => record.status === 'pending' && record.file)

    // Check if any pending files exceed the size limit
    const oversizedFiles = pendingFiles.filter((record) => {
      const fileSize = record.file?.size
      return fileSize !== undefined && fileSize > maxFileSizeBytes
    })

    if (oversizedFiles.length > 0) {
      const maxSizeFormatted = formatFileSize(maxFileSizeBytes, translate)
      return translate('File/s exceeds the maximum size of %{maxSize}', {
        maxSize: maxSizeFormatted,
      })
    }

    return undefined
  }
}
